// Under a transcript: where he prompts a session, with Claude Code's keys
// (Poulpe-703). ↵ sends, and so does Send; ⇧↵ is a new line. While the
// session works, ↵ sends into the turn that runs (`priority: now`): an agent
// that can be steered takes it there, one that cannot has it when the turn
// ends. ⌘↵ (Ctrl+↵ off the Mac), and Queue, send it as a follow-up
// (`later`): it waits above the box (queue.tsx, Poulpe-430), where it can be
// sent now, moved or taken back, and goes when the turn ends. While the
// session is idle both send at once. Stop (⌘.) interrupts the turn, which is
// `interrupt_session`. Ending the session is the strip's End, never here.
// What he types is kept for the session, across a reload, until it is sent
// (drafts.ts). An unresolved send and its files are kept in IndexedDB until
// the server accepts it (pendingPrompt.ts). A session that ended and may be
// resumed has Resume instead of the box (ResumeButton.tsx, Poulpe-425).
//
// A slash at the start of the box asks for one of the agent's commands
// (commands.ts): the list above the box is narrowed as he types, ↑ and ↓
// move in it, ↵ or ⇥ writes the chosen name into the box, Esc closes it.
// ⌘↵ queues what the box holds, as ever; ↵ sends it once the list is closed.
//
// Files and images are attached with the clip, dropped on the form or pasted
// into the box (attachments.tsx): a chip each, removable, until the prompt
// goes. A prompt of attachments alone is a prompt.
//
// A prompt sent again after a failure, the same, goes with the same
// mutation id, so the server keeps it once (pendingPrompt.ts). A refusal
// is said in a toast, and the prompt comes back to the box unless he has
// typed another since. Moved from today's Code tab (code/Composer.tsx
// there). Model, effort and mode use actual confirmed session options;
// requested settings never become current merely because an API job exists.
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ClipboardEvent, type DragEvent, type FormEvent, type KeyboardEvent, type ReactNode } from 'react'
import { errorMessage } from '../api/client'
import type { Runner, Session, UnsentPrompt } from '../api/types'
import { uuid4 } from '../api/uuid'
import { useMounted } from '../components/layout'
import { toast } from '../components/toasts'
import { needsServer } from '../components/needsServer'
import type { AgentCommand } from '../sessions/info'
import { useBoardState, useStore } from '../store/context'
import { cx } from '../transcript/bits'
import { canInteract, useTabScope } from '../workspace/context'
import { AttachedFiles, ClipIcon, droppedFiles, filesKey, NO_FILES, pastedFiles, withFiles } from './attachments'
import { commandQuery, commandText, matchCommands } from './commands'
import { readDraft, saveDraft } from './drafts'
import { awaitingPerson } from '../sessions/attention'
import { isBusy, runnerClosed, stateText } from './format'
import { SendIcon, StopIcon } from './icons'
import { PromptInk } from './PromptInk'
import { filesOf, PromptQueue, withUnsent } from './queue'
import { ResumeButton } from './ResumeButton'
import { TerminalHandoff, inTerminal } from './TerminalHandoff'
import { ChangeFolder } from './ChangeFolder'
import { SessionControls } from './SessionControls'
import { usePendingPrompt } from './pendingPrompt'
import { useLocalWrites } from './localWrites'
import { MAX_LOCAL_PROMPT, writeLocally } from '../api/shellLocalReads'

export interface ComposerProps {
  session: Session
  /** The session's runner, once the runners are read. */
  runner?: Runner
  /** It has an open question. */
  asking?: boolean
  /** The agent's commands; null until it says them. */
  commands: readonly AgentCommand[] | null
}

/** The tallest the box grows before it scrolls. */
const MAX_PROMPT_PX = 260

const toastError = (action: string, err: unknown) => toast(`${action}: ${errorMessage(err)}`, 'error')

function Line({ children }: { children: ReactNode }) {
  return (
    <div className="code-composer code-composer-line" role="note">
      {children}
    </div>
  )
}

export function Composer({ session, runner, asking = false, commands }: ComposerProps) {
  const store = useStore()
  const { me, offline } = useBoardState()
  const member = me?.member?.id ?? null
  const mounted = useMounted()
  const box = useRef<HTMLTextAreaElement>(null)
  const ink = useRef<HTMLDivElement>(null)
  const hintId = useId()
  const menuId = useId()
  const [draft, setDraft] = useState(() => readDraft(member, session.id))
  const latest = useRef(draft)
  const [stopping, setStopping] = useState(false)
  // The files that wait to go with the prompt, and whether one is held over the form.
  const [files, setFilesState] = useState<readonly File[]>(NO_FILES)
  const filesNow = useRef(files)
  const [dropping, setDropping] = useState(false)
  const picker = useRef<HTMLInputElement>(null)
  // The list's own state: which entry is chosen, and the text at which he closed it (it stays closed until the box holds another).
  const [chosen, setChosen] = useState(0)
  const [closedAt, setClosedAt] = useState<string | null>(null)
  const ended = session.state === 'ended'
  const closed = ended ? null : runnerClosed(runner)
  const busy = isBusy(session)
  const continuing = !asking && awaitingPerson(session)
  const text = draft.trim()
  const settingsBlocked = !!session.pending_place || session.settings?.change?.state === 'refused' || session.settings?.change?.state === 'unconfirmed'
  const query = commands ? commandQuery(draft) : null
  const matches = useMemo(() => (query === null || !commands ? [] : matchCommands(commands, query)), [commands, query])
  const menu = query !== null && closedAt !== draft && matches.length > 0
  const at = Math.min(chosen, matches.length - 1)

  const setFiles = (next: readonly File[]) => {
    filesNow.current = next
    setFilesState(next)
  }
  const onDraft = (next: string) => {
    latest.current = next
    setDraft(next)
    saveDraft(member, session.id, next)
  }
  const { pending, loading, sending, send: sendPrompt, retry } = usePendingPrompt(session.id, {
    take: (sent, attached) => {
      // A newer draft stays while the saved mutation is reconciled.
      if (latest.current.trim() === sent) onDraft('')
      if (JSON.stringify(filesKey(filesNow.current)) === JSON.stringify(filesKey(attached))) setFiles(NO_FILES)
    },
    restore: (sent, attached) => {
      if (latest.current.trim() === '') onDraft(sent)
      if (filesNow.current.length === 0) setFiles(attached)
    },
  })
  // Offline, a session its owner started on this Mac's runner still takes a prompt, through that runner (Poulpe-830).
  const here = useLocalWrites(session, offline)
  const [writingHere, setWritingHere] = useState(false)
  const blocked = offline && !here
  useEffect(() => setChosen(0), [query])

  // The box is as tall as what it holds, up to a limit.
  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    el.style.height = 'auto'
    if (el.scrollHeight > 0) el.style.height = `${Math.min(el.scrollHeight + 2, MAX_PROMPT_PX)}px`
    if (ink.current) ink.current.scrollTop = el.scrollTop
  }, [draft])

  // A session just opened takes what he types.
  useEffect(() => {
    box.current?.focus({ preventScroll: true })
  }, [session.id])

  const stop = () => {
    if (stopping || store.getState().offline) return
    setStopping(true)
    // The prompts that wait behind the turn are not sent when it is stopped (Poulpe-430).
    const waiting = session.queued?.length ?? 0
    store
      .sendSessionOp({ mutation_id: uuid4(), op: 'interrupt_session', args: { session_id: session.id } })
      .then(() => {
        if (waiting > 0) toast(`${waiting} queued prompt${waiting === 1 ? ' was' : 's were'} not sent.`)
      })
      .catch((err: unknown) => toastError('Could not stop the session', err))
      .finally(() => {
        if (mounted.current) setStopping(false)
      })
  }

  // ⌘. stops the turn from anywhere on the page: in the Mac app's workspace, the page of the tab in use.
  const scope = useTabScope()
  const stopRef = useRef(stop)
  useEffect(() => {
    stopRef.current = stop
  })
  const canStop = busy && !ended && closed === null
  useEffect(() => {
    if (!canStop || offline) return
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing || event.keyCode === 229 || !canInteract(scope) || document.querySelector('[aria-modal="true"], dialog[open]') || box.current?.closest('[inert]')) return
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key === '.') {
        event.preventDefault()
        stopRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [canStop, offline, scope])

  if (ended) return <Line>{session.ended?.outcome === 'failed' ? 'This session failed.' : session.ended?.outcome === 'interrupted' ? 'This session was interrupted.' : 'This session has ended.'}{['pending', 'applying', 'unconfirmed'].includes(session.settings?.change?.state ?? '') && <span className="text-danger" role="alert"> The settings change could not be confirmed before the session ended.</span>}{session.resumable && <> <ResumeButton session={session} /><ChangeFolder session={session} disabled={offline}/></>}</Line>
  if (closed) return <Line>{closed}</Line>
  if (inTerminal(session)) return <Line><TerminalHandoff session={session} disabled={offline}/></Line>

  // `now` goes into the turn that runs, `later` behind it; while none runs, both go at once.
  const send = (priority: 'now' | 'later' = 'now') => {
    if ((!text && files.length === 0) || loading || sending || writingHere || settingsBlocked) return
    if (store.getState().offline) {
      // Through this Mac's runner: at once, words alone; queueing and files need the server.
      if (here && priority === 'now' && files.length === 0) void sendHere(text)
      return
    }
    void sendPrompt(text, files, priority)
  }
  const sendHere = async (sent: string) => {
    if (new TextEncoder().encode(sent).length > MAX_LOCAL_PROMPT) {
      toast('The prompt is too long to send through this Mac’s runner while offline.', 'error')
      return
    }
    setWritingHere(true)
    const outcome = await writeLocally({ write: 'prompt', session: session.id, prompt: sent })
    if (mounted.current) setWritingHere(false)
    if (outcome.done) {
      if (latest.current.trim() === sent) onDraft('')
      return
    }
    toast(outcome.reason ? `This Mac’s runner did not take the prompt: ${outcome.reason}` : 'This Mac’s runner did not answer: the prompt may not have been sent.', 'error')
  }
  // One word for all that was not attached: the page shows a few toasts at a time.
  const attach = (found: readonly File[], folders = 0) => {
    const next = withFiles(filesNow.current, found, folders)
    if (next.refused.length > 0) toast(next.refused.join(' '), 'error')
    if (next.files !== filesNow.current) setFiles(next.files)
  }
  const holdsFiles = (event: DragEvent) => Array.from(event.dataTransfer.types).includes('Files')
  const onDragOver = (event: DragEvent) => {
    if (!holdsFiles(event)) return
    // Without this the window would open the file in place of the board.
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
    if (!dropping) setDropping(true)
  }
  const onDragLeave = (event: DragEvent<HTMLFormElement>) => {
    if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) setDropping(false)
  }
  const onDrop = (event: DragEvent) => {
    if (!holdsFiles(event)) return
    event.preventDefault()
    setDropping(false)
    const dropped = droppedFiles(event.dataTransfer)
    attach(dropped.files, dropped.folders)
    box.current?.focus({ preventScroll: true })
  }
  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = pastedFiles(event.clipboardData)
    if (pasted.length === 0) return
    event.preventDefault()
    attach(pasted)
  }
  // A prompt taken back from the queue comes into the box, ahead of what he has begun to write, with its files.
  const restore = (unsent: UnsentPrompt) => {
    onDraft(withUnsent(latest.current, unsent))
    attach(filesOf(unsent.attachments))
    box.current?.focus({ preventScroll: true })
  }
  const pick = (command: AgentCommand) => {
    onDraft(commandText(command))
    box.current?.focus({ preventScroll: true })
  }
  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    send('now')
  }
  const canSend = !((!text && files.length === 0) || loading || sending || writingHere || blocked || (offline && files.length > 0) || settingsBlocked)
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return
    if (menu && !event.metaKey && !event.ctrlKey && !event.altKey) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        setChosen((at + (event.key === 'ArrowDown' ? 1 : matches.length - 1)) % matches.length)
        return
      }
      if ((event.key === 'Enter' || event.key === 'Tab') && !event.shiftKey) {
        event.preventDefault()
        pick(matches[at]!)
        return
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        setClosedAt(draft)
        return
      }
    }
    if (event.key !== 'Enter' || event.altKey || event.shiftKey) return
    event.preventDefault()
    send(event.metaKey || event.ctrlKey ? 'later' : 'now')
  }

  return (
    <form className={cx('code-composer', busy && 'is-busy', dropping && 'is-dropping')} onSubmit={onSubmit} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} aria-label="Prompt the session">
      <PromptQueue session={session} onUnsent={restore} />
      {pending && !sending && (
        <div className="code-send-recovery" role="alert">
          <p>{pending.attachments && !pending.refused ? 'The prompt’s status is uncertain.' : 'The prompt was not sent.'}</p>
          <pre>{pending.text}</pre>
          {!pending.attachments && pending.files.filter(file => !files.some(found => found.name === file.name && found.size === file.size)).map((file, index) => <p key={index}>“{file.name}” could not be read: attach it again.</p>)}
          {pending.attachments && <>
            {pending.files.length > 0 && <ul>{pending.files.map((file, index) => <li key={index}>{file.name}</li>)}</ul>}
            <button type="button" className="button button-quiet" disabled={offline || loading} onClick={() => void retry()} {...needsServer(offline)}>Try again</button>
          </>}
        </div>
      )}
      <AttachedFiles files={files} onRemove={(index) => setFiles(files.filter((_, at) => at !== index))} />
      {menu && (
        <ul className="code-commands" id={menuId} role="listbox" aria-label="Commands of this session">
          {matches.map((command, place) => (
            <li
              key={command.name}
              id={`${menuId}-${place}`}
              role="option"
              aria-selected={place === at}
              className={cx(place === at && 'is-chosen')}
              // The box keeps the keyboard: a press chooses without taking it away.
              onMouseDown={(event) => {
                event.preventDefault()
                pick(command)
              }}
              onMouseEnter={() => setChosen(place)}
            >
              <span className="code-command-name">/{command.name}</span>
              {command.hint && <span className="code-command-hint">{command.hint}</span>}
              <span className="code-command-text">{command.description}</span>
            </li>
          ))}
        </ul>
      )}
      {/* The box's letters are transparent: the ink behind draws them, backtick code marked as it is typed. */}
      <div className="code-prompt-box">
        <PromptInk ref={ink} text={draft} />
        <textarea
          ref={box}
          className="textarea code-prompt"
          value={draft}
          onChange={(event) => onDraft(event.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onScroll={(event) => {
            if (ink.current) ink.current.scrollTop = event.currentTarget.scrollTop
          }}
          rows={2}
          aria-label="Prompt"
          aria-describedby={hintId}
          aria-autocomplete={query !== null ? 'list' : undefined}
          aria-controls={menu ? menuId : undefined}
          aria-activedescendant={menu ? `${menuId}-${at}` : undefined}
          aria-keyshortcuts="Enter Meta+Enter Control+Enter"
          placeholder={busy ? `${stateText(session, asking)}: ↵ sends into the turn, or for its end if the agent cannot take it now; ⌘↵ queues a follow-up` : continuing ? 'Ask a follow-up question or give the next instruction' : 'Prompt the session'}
        />
      </div>
      <div className="code-composer-bar">
        <span className="visually-hidden" id={hintId}>
          {busy
            ? 'Return sends it into the running turn; an agent that cannot be steered has it when the turn ends. Command Return queues it as a follow-up, Command Period stops the turn.'
            : 'Return sends, Shift Return adds a new line.'}
          {' Drop a file on the box, or paste an image, to attach it.'}
        </span>
        <input
          ref={picker}
          type="file"
          multiple
          hidden
          onChange={(event) => {
            attach(Array.from(event.currentTarget.files ?? []))
            event.currentTarget.value = ''
          }}
        />
        <button type="button" className="button button-quiet" disabled={sending} onClick={() => picker.current?.click()}>
          <ClipIcon />
          Attach files
        </button>
        <ChangeFolder session={session} disabled={offline || sending || (asking && session.place?.kind !== 'checkout')}/>
        <TerminalHandoff session={session} disabled={offline || sending}/>
        <SessionControls session={session} runner={runner} disabled={offline || sending}/>
        <div className="code-composer-buttons">
          {canStop && (
            <button type="button" className="button code-stop" disabled={stopping || offline} aria-keyshortcuts="Meta+." title="Stop its turn (⌘.)" {...needsServer(offline)} onClick={stop}>
              <StopIcon size={12} />
              Stop
              <kbd aria-hidden="true">⌘.</kbd>
            </button>
          )}
          {busy && (
            <button type="button" className="button button-quiet code-queue" disabled={!canSend || offline} aria-keyshortcuts="Meta+Enter" title="Queue it for the turn’s end (⌘↵)" {...needsServer(offline)} onClick={() => send('later')}>
              Queue
              <kbd aria-hidden="true">⌘↵</kbd>
            </button>
          )}
          <button type="submit" className="button button-primary code-send" disabled={!canSend} aria-keyshortcuts="Enter" title={busy ? 'Send into the running turn (↵); an agent that cannot be steered has it when the turn ends' : continuing ? 'Continue (↵)' : 'Send (↵)'} {...needsServer(blocked || (offline && files.length > 0))}>
            <SendIcon size={13} />
            {continuing ? 'Continue' : 'Send'}
            <kbd aria-hidden="true">↵</kbd>
          </button>
        </div>
      </div>
    </form>
  )
}
